/**
 * Theme configuration
 * Reads theme colors from environment variables
 * Works with both local development and AWS Amplify
 * 
 * Note: In Create React App, environment variables are loaded at build time.
 * You must restart the dev server (npm start) after changing .env files.
 * 
 * File loading priority (higher priority overrides lower):
 * 1. .env.development.local, .env.local (ignored by git)
 * 2. .env.development, .env.production (based on NODE_ENV)
 * 3. .env (lowest priority)
 */

import { theme } from 'antd';

// Debug: Log environment variables (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('=== Environment Variables Debug ===');
  console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  console.log('REACT_APP_THEME_PRIMARY_COLOR:', process.env.REACT_APP_THEME_PRIMARY_COLOR);
  console.log('REACT_APP_THEME_SECONDARY_COLOR:', process.env.REACT_APP_THEME_SECONDARY_COLOR);
  console.log('REACT_APP_THEME_TERTIARY_COLOR:', process.env.REACT_APP_THEME_TERTIARY_COLOR);
  console.log('REACT_APP_THEME_TERTIARY_TEXT:', process.env.REACT_APP_THEME_TERTIARY_TEXT);
  console.log('REACT_APP_THEME_BACKGROUND_COLOR:', process.env.REACT_APP_THEME_BACKGROUND_COLOR);
  console.log('REACT_APP_THEME_DARK_BACKGROUND:', process.env.REACT_APP_THEME_DARK_BACKGROUND);
  console.log('REACT_APP_THEME_PRIMARY_COLOR_DARK:', process.env.REACT_APP_THEME_PRIMARY_COLOR_DARK);
  console.log('REACT_APP_THEME_SECONDARY_COLOR_DARK:', process.env.REACT_APP_THEME_SECONDARY_COLOR_DARK);
  console.log('REACT_APP_THEME_TERTIARY_COLOR_DARK:', process.env.REACT_APP_THEME_TERTIARY_COLOR_DARK);
  console.log('REACT_APP_THEME_TERTIARY_TEXT_DARK:', process.env.REACT_APP_THEME_TERTIARY_TEXT_DARK);
  console.log('===================================');
}

export const themeConfig = {
  // API Configuration
  apiUrl: process.env.REACT_APP_API_URL || 'https://api.keshavinteriors.com',
  
  // Theme Colors (Light Mode)
  colors: {
    primary: process.env.REACT_APP_THEME_PRIMARY_COLOR || '#b79264',
    secondary: process.env.REACT_APP_THEME_SECONDARY_COLOR || '#f7f3e7',
    tertiary: process.env.REACT_APP_THEME_TERTIARY_COLOR || '#ffffff',
    tertiaryText: process.env.REACT_APP_THEME_TERTIARY_TEXT || '#000000',
    backgroundColor: process.env.REACT_APP_THEME_BACKGROUND_COLOR || '#f7f3e7',
    darkBackground: process.env.REACT_APP_THEME_DARK_BACKGROUND || '#111111',
  },
  
  // Dark Mode Theme Colors (variants of primary, secondary, tertiary, and text)
  darkColors: {
    primary: process.env.REACT_APP_THEME_PRIMARY_COLOR_DARK || '#5a3316',
    secondary: process.env.REACT_APP_THEME_SECONDARY_COLOR_DARK || '#2d2d2d',
    tertiary: process.env.REACT_APP_THEME_TERTIARY_COLOR_DARK || '#1a1a1a',
    tertiaryText: process.env.REACT_APP_THEME_TERTIARY_TEXT_DARK || '#f7f3e7',
  },
  
  // Status Colors
  status: {
    success: process.env.REACT_APP_THEME_SUCCESS_COLOR || '#52c41a',
    successDark: process.env.REACT_APP_THEME_SUCCESS_COLOR_DARK || '#3f8600',
    successLight: process.env.REACT_APP_THEME_SUCCESS_COLOR_LIGHT || '#389e0d',
    successBg: process.env.REACT_APP_THEME_SUCCESS_BG || '#f6ffed',
    successBorder: process.env.REACT_APP_THEME_SUCCESS_BORDER || '#b7eb8f',
    error: process.env.REACT_APP_THEME_ERROR_COLOR || '#ff4d4f',
    errorDark: process.env.REACT_APP_THEME_ERROR_COLOR_DARK || '#cf1322',
    errorBg: process.env.REACT_APP_THEME_ERROR_BG || 'rgb(252,229,227)',
    warning: process.env.REACT_APP_THEME_WARNING_COLOR || '#faad14',
    warningDark: process.env.REACT_APP_THEME_WARNING_COLOR_DARK || '#d46b08',
    warningLight: process.env.REACT_APP_THEME_WARNING_COLOR_LIGHT || '#fa8c16',
    warningBg: process.env.REACT_APP_THEME_WARNING_BG || '#fff7e6',
    warningBorder: process.env.REACT_APP_THEME_WARNING_BORDER || '#ffd591',
    info: process.env.REACT_APP_THEME_INFO_COLOR || '#1890ff',
    infoDark: process.env.REACT_APP_THEME_INFO_COLOR_DARK || '#1677ff',
    infoBg: process.env.REACT_APP_THEME_INFO_BG || '#e6f7ff',
  },
  
  // Text Colors
  text: {
    muted: process.env.REACT_APP_THEME_TEXT_MUTED || '#666',
    mutedLight: process.env.REACT_APP_THEME_TEXT_MUTED_LIGHT || '#999',
    mutedMedium: process.env.REACT_APP_THEME_TEXT_MUTED_MEDIUM || '#888',
    mutedVeryLight: process.env.REACT_APP_THEME_TEXT_MUTED_VERY_LIGHT || '#ccc',
    mutedDark: process.env.REACT_APP_THEME_TEXT_MUTED_DARK || '#595959',
    placeholder: process.env.REACT_APP_THEME_TEXT_PLACEHOLDER || '#bfbfbf',
    placeholderDark: process.env.REACT_APP_THEME_TEXT_PLACEHOLDER_DARK || '#8c8c8c',
  },
  
  // Background Colors
  background: {
    tableHeader: process.env.REACT_APP_THEME_BG_TABLE_HEADER || '#f5f5f5',
    tableRowEven: process.env.REACT_APP_THEME_BG_TABLE_ROW_EVEN || '#f9f9f9',
    tableRowOdd: process.env.REACT_APP_THEME_BG_TABLE_ROW_ODD || '#ffffff',
    card: process.env.REACT_APP_THEME_BG_CARD || '#fafafa',
    container: process.env.REACT_APP_THEME_BG_CONTAINER || '#f0f0f0',
    white: process.env.REACT_APP_THEME_BG_WHITE || '#ffffff',
    beige: process.env.REACT_APP_THEME_BG_BEIGE || '#f7f3e7',
  },
  
  // Border Colors
  border: {
    default: process.env.REACT_APP_THEME_BORDER_DEFAULT || '#d9d9d9',
    light: process.env.REACT_APP_THEME_BORDER_LIGHT || '#ddd',
    muted: process.env.REACT_APP_THEME_BORDER_MUTED || '#ccc',
  },
  
  // Box Shadow
  shadow: {
    small: process.env.REACT_APP_THEME_SHADOW_SMALL || '0 2px 5px rgba(0,0,0,0.1)',
    medium: process.env.REACT_APP_THEME_SHADOW_MEDIUM || '0 2px 6px rgba(0,0,0,0.1)',
    large: process.env.REACT_APP_THEME_SHADOW_LARGE || '0 4px 12px rgba(0,0,0,0.1)',
    none: process.env.REACT_APP_THEME_SHADOW_NONE || 'none',
  },
  
  // Login Page Colors
  login: {
    background: process.env.REACT_APP_THEME_LOGIN_BG || '#f0f2f5',
    gradientStart: process.env.REACT_APP_THEME_LOGIN_GRADIENT_START || '#667eea',
    gradientEnd: process.env.REACT_APP_THEME_LOGIN_GRADIENT_END || '#764ba2',
    textDark: process.env.REACT_APP_THEME_LOGIN_TEXT_DARK || '#333',
  },
};

// Log final theme config in development
if (process.env.NODE_ENV === 'development') {
  console.log('Theme config:', themeConfig);
}

/**
 * Get Ant Design theme configuration
 * @param {boolean} isDarkMode - Whether dark mode is active
 */
export const getAntDesignTheme = (isDarkMode = false) => {
  const colors = isDarkMode ? themeConfig.darkColors : themeConfig.colors;
  
  // Base tokens - these will be automatically adjusted by darkAlgorithm in dark mode
  const baseTokens = {
    // Primary colors
    colorPrimary: colors.primary || themeConfig.colors.primary,
    
    // Status colors
    colorSuccess: themeConfig.status.success,
    colorError: themeConfig.status.error,
    colorWarning: themeConfig.status.warning,
    colorInfo: themeConfig.status.info,
    
    // Background colors
    colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
    colorBgLayout: colors.secondary || themeConfig.colors.secondary,
    colorBgElevated: colors.tertiary || themeConfig.colors.tertiary,
    
    // Text colors
    colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
    colorTextSecondary: themeConfig.text.muted,
    colorTextTertiary: themeConfig.text.mutedLight,
    colorTextQuaternary: themeConfig.text.mutedVeryLight,
    colorTextDisabled: themeConfig.text.mutedVeryLight,
    
    // Border colors
    colorBorder: themeConfig.border.default,
    colorBorderSecondary: themeConfig.border.light,
    
    // Placeholder
    colorTextPlaceholder: isDarkMode ? themeConfig.text.placeholderDark : themeConfig.text.placeholder,
  };

  // Component customizations
  const components = {
    // Button customizations
    Button: {
      defaultBg: isDarkMode ? themeConfig.darkColors.tertiary : themeConfig.background.white,
      defaultColor: isDarkMode ? themeConfig.darkColors.tertiaryText : themeConfig.text.mutedDark,
      defaultBorderColor: themeConfig.border.default,
      defaultHoverBg: themeConfig.background.container,
      defaultHoverBorderColor: colors.primary || themeConfig.colors.primary,
      defaultHoverColor: colors.primary || themeConfig.colors.primary,
      defaultActiveBg: themeConfig.background.container,
      defaultActiveBorderColor: colors.primary || themeConfig.colors.primary,
      defaultActiveColor: colors.primary || themeConfig.colors.primary,
    },
    
    // Input customizations
    Input: {
      colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorTextPlaceholder: isDarkMode ? themeConfig.text.placeholderDark : themeConfig.text.placeholder,
      colorBorder: themeConfig.border.default,
      colorBorderHover: colors.primary || themeConfig.colors.primary,
      activeBorderColor: colors.primary || themeConfig.colors.primary,
    },
    
    // Select customizations
    Select: {
      colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorTextPlaceholder: isDarkMode ? themeConfig.text.placeholderDark : themeConfig.text.placeholder,
      colorBorder: themeConfig.border.default,
      colorBorderHover: colors.primary || themeConfig.colors.primary,
    },
    
    // DatePicker customizations
    DatePicker: {
      colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorTextPlaceholder: isDarkMode ? themeConfig.text.placeholderDark : themeConfig.text.placeholder,
      colorBorder: themeConfig.border.default,
      colorBorderHover: colors.primary || themeConfig.colors.primary,
      activeBorderColor: colors.primary || themeConfig.colors.primary,
    },
    
    // InputNumber customizations
    InputNumber: {
      colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorTextPlaceholder: isDarkMode ? themeConfig.text.placeholderDark : themeConfig.text.placeholder,
      colorBorder: themeConfig.border.default,
      colorBorderHover: colors.primary || themeConfig.colors.primary,
      activeBorderColor: colors.primary || themeConfig.colors.primary,
    },
    
    // Note: TextArea uses the same Input component customization above
    
    // Tag customizations - custom colors for each variant
    Tag: {
      // Default tag (gray)
      defaultBg: themeConfig.background.container,
      defaultColor: themeConfig.text.mutedDark,
      defaultBorderColor: themeConfig.border.default,
      
      // Red tag
      redBg: themeConfig.status.errorBg,
      redColor: isDarkMode ? '#ff7875' : themeConfig.status.errorDark,
      redBorderColor: themeConfig.status.error,
      
      // Green tag
      greenBg: themeConfig.status.successBg,
      greenColor: isDarkMode ? '#73d13d' : themeConfig.status.successDark,
      greenBorderColor: themeConfig.status.success,
      
      // Blue tag
      blueBg: themeConfig.status.infoBg,
      blueColor: isDarkMode ? '#69c0ff' : themeConfig.status.infoDark,
      blueBorderColor: themeConfig.status.info,
      
      // Orange tag
      orangeBg: isDarkMode ? 'rgba(250, 173, 20, 0.2)' : themeConfig.status.warningBg,
      orangeColor: isDarkMode ? '#ffc53d' : themeConfig.status.warningDark,
      orangeBorderColor: themeConfig.status.warning,
      
      // Purple tag
      purpleBg: isDarkMode ? 'rgba(146, 84, 222, 0.2)' : '#f9f0ff',
      purpleColor: isDarkMode ? '#b37feb' : '#722ed1',
      purpleBorderColor: '#9254de',
    },
    
    // Table customizations
    Table: {
      headerBg: isDarkMode ? themeConfig.darkColors.tertiary : themeConfig.background.tableHeader,
      headerColor: colors.tertiaryText || themeConfig.colors.tertiaryText,
      rowHoverBg: colors.secondary || themeConfig.colors.secondary,
      colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorBorderSecondary: themeConfig.border.light,
    },
    
    // Card customizations
    Card: {
      colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorTextHeading: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorBorderSecondary: themeConfig.border.default,
    },
    
    // Modal customizations
    Modal: {
      contentBg: colors.tertiary || themeConfig.colors.tertiary,
      headerBg: colors.tertiary || themeConfig.colors.tertiary,
      titleColor: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorIcon: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorIconHover: colors.primary || themeConfig.colors.primary,
      footerBg: colors.tertiary || themeConfig.colors.tertiary,
      colorBorderSecondary: themeConfig.border.default,
    },
    
    // Pagination customizations
    Pagination: {
      colorBgContainer: colors.tertiary || themeConfig.colors.tertiary,
      colorText: colors.tertiaryText || themeConfig.colors.tertiaryText,
      colorTextDisabled: themeConfig.text.muted,
      colorBorder: themeConfig.border.default,
      itemActiveBg: colors.primary || themeConfig.colors.primary,
      itemActiveColorDisabled: isDarkMode ? themeConfig.darkColors.tertiaryText : themeConfig.colors.tertiaryText,
      itemLinkBg: colors.tertiary || themeConfig.colors.tertiary,
      itemBg: colors.tertiary || themeConfig.colors.tertiary,
    },
    
    // Form customizations
    Form: {
      labelColor: colors.tertiaryText || themeConfig.colors.tertiaryText,
      itemMarginBottom: 24,
    },
  };

  return {
    algorithm: isDarkMode ? [theme.darkAlgorithm] : [theme.defaultAlgorithm],
    token: baseTokens,
    components,
  };
};

/**
 * Apply CSS variables to document root
 * This allows CSS files to use the theme colors via CSS variables
 * @param {boolean} isDarkMode - Whether dark mode is active
 */
export const applyCSSVariables = (isDarkMode = false) => {
  const root = document.documentElement;
  const colors = isDarkMode ? themeConfig.darkColors : themeConfig.colors;
  
  // Apply primary, secondary, and tertiary colors (light or dark variants)
  root.style.setProperty('--primary-color', colors.primary || themeConfig.colors.primary);
  root.style.setProperty('--secondary-color', colors.secondary || themeConfig.colors.secondary);
  root.style.setProperty('--tertiary-color', colors.tertiary || themeConfig.colors.tertiary);
  
  // Apply text color based on dark mode (white for dark mode, black for light mode)
  root.style.setProperty('--tertiary-text', colors.tertiaryText || themeConfig.colors.tertiaryText);
  
  // Background colors
  root.style.setProperty('--background-color', isDarkMode ? themeConfig.colors.darkBackground : themeConfig.colors.backgroundColor);
  root.style.setProperty('--dark-background', themeConfig.colors.darkBackground);
  
  // Add dark variant CSS variables for reference
  root.style.setProperty('--primary-color-dark', themeConfig.darkColors.primary);
  root.style.setProperty('--secondary-color-dark', themeConfig.darkColors.secondary);
  root.style.setProperty('--tertiary-color-dark', themeConfig.darkColors.tertiary);
  root.style.setProperty('--tertiary-text-dark', themeConfig.darkColors.tertiaryText);
  
  // Status colors
  root.style.setProperty('--success-color', themeConfig.status.success);
  root.style.setProperty('--success-color-dark', themeConfig.status.successDark);
  root.style.setProperty('--success-color-light', themeConfig.status.successLight);
  root.style.setProperty('--success-bg', themeConfig.status.successBg);
  root.style.setProperty('--success-border', themeConfig.status.successBorder);
  root.style.setProperty('--error-color', themeConfig.status.error);
  root.style.setProperty('--error-color-dark', themeConfig.status.errorDark);
  root.style.setProperty('--error-bg', themeConfig.status.errorBg);
  root.style.setProperty('--warning-color', themeConfig.status.warning);
  root.style.setProperty('--warning-color-dark', themeConfig.status.warningDark);
  root.style.setProperty('--warning-color-light', themeConfig.status.warningLight);
  root.style.setProperty('--warning-bg', themeConfig.status.warningBg);
  root.style.setProperty('--warning-border', themeConfig.status.warningBorder);
  root.style.setProperty('--info-color', themeConfig.status.info);
  root.style.setProperty('--info-color-dark', themeConfig.status.infoDark);
  root.style.setProperty('--info-bg', themeConfig.status.infoBg);
  
  // Text colors
  root.style.setProperty('--text-muted', themeConfig.text.muted);
  root.style.setProperty('--text-muted-light', themeConfig.text.mutedLight);
  root.style.setProperty('--text-muted-medium', themeConfig.text.mutedMedium);
  root.style.setProperty('--text-muted-very-light', themeConfig.text.mutedVeryLight);
  root.style.setProperty('--text-muted-dark', themeConfig.text.mutedDark);
  
  // Placeholder colors (adapts to dark mode)
  root.style.setProperty('--text-placeholder', isDarkMode ? themeConfig.text.placeholderDark : themeConfig.text.placeholder);
  
  // Tag text colors (adapts to dark mode)
  // Light mode: darker version of background color
  // Dark mode: lighter version for better contrast
  // Note: Default tags always use dark text since they have light gray background
  if (isDarkMode) {
    // Dark mode: use lighter colors for better contrast
    root.style.setProperty('--tag-red-text', '#ff7875');
    root.style.setProperty('--tag-green-text', '#73d13d');
    root.style.setProperty('--tag-blue-text', '#69c0ff');
    root.style.setProperty('--tag-orange-text', '#ffc53d');
    root.style.setProperty('--tag-purple-text', '#b37feb');
    // Default tag: always use dark text since background is always light gray
    root.style.setProperty('--tag-default-text', themeConfig.text.mutedDark);
    // Purple tag background and border (dark mode: dark purple background)
    root.style.setProperty('--tag-purple-bg', 'rgba(146, 84, 222, 0.2)');
    root.style.setProperty('--tag-purple-border', '#9254de');
    // Orange tag background and border (dark mode: dark orange background)
    root.style.setProperty('--tag-orange-bg', 'rgba(250, 173, 20, 0.2)');
    root.style.setProperty('--tag-orange-border', '#faad14');
  } else {
    // Light mode: use darker colors for better contrast
    root.style.setProperty('--tag-red-text', themeConfig.status.errorDark);
    root.style.setProperty('--tag-green-text', themeConfig.status.successDark);
    root.style.setProperty('--tag-blue-text', themeConfig.status.infoDark);
    root.style.setProperty('--tag-orange-text', themeConfig.status.warningDark);
    root.style.setProperty('--tag-purple-text', '#722ed1');
    // Default tag: always use dark text since background is always light gray
    root.style.setProperty('--tag-default-text', themeConfig.text.mutedDark);
    // Purple tag background and border (light mode: light purple background)
    root.style.setProperty('--tag-purple-bg', '#f9f0ff');
    root.style.setProperty('--tag-purple-border', '#9254de');
    // Orange tag background and border (light mode: light orange background)
    root.style.setProperty('--tag-orange-bg', themeConfig.status.warningBg);
    root.style.setProperty('--tag-orange-border', themeConfig.status.warning);
  }
  
  // Background colors
  root.style.setProperty('--bg-table-header', themeConfig.background.tableHeader);
  root.style.setProperty('--bg-table-row-even', themeConfig.background.tableRowEven);
  root.style.setProperty('--bg-table-row-odd', themeConfig.background.tableRowOdd);
  root.style.setProperty('--bg-card', themeConfig.background.card);
  root.style.setProperty('--bg-container', themeConfig.background.container);
  root.style.setProperty('--bg-white', themeConfig.background.white);
  root.style.setProperty('--bg-beige', themeConfig.background.beige);
  
  // Border colors
  root.style.setProperty('--border-default', themeConfig.border.default);
  root.style.setProperty('--border-light', themeConfig.border.light);
  root.style.setProperty('--border-muted', themeConfig.border.muted);
  
  // Box shadows
  root.style.setProperty('--shadow-small', themeConfig.shadow.small);
  root.style.setProperty('--shadow-medium', themeConfig.shadow.medium);
  root.style.setProperty('--shadow-large', themeConfig.shadow.large);
  root.style.setProperty('--shadow-none', themeConfig.shadow.none);
  
  // Login page colors
  root.style.setProperty('--login-bg', themeConfig.login.background);
  root.style.setProperty('--login-gradient-start', themeConfig.login.gradientStart);
  root.style.setProperty('--login-gradient-end', themeConfig.login.gradientEnd);
  root.style.setProperty('--login-text-dark', themeConfig.login.textDark);
  
  // Button backgrounds (adapts to dark mode)
  // Light mode: lighter backgrounds, dark mode: darker backgrounds
  if (isDarkMode) {
    // Dark mode: use darker backgrounds for default buttons with white text
    // Use tertiary-color-dark (dark background) instead of container (light gray)
    root.style.setProperty('--btn-default-bg', themeConfig.darkColors.tertiary);
    root.style.setProperty('--btn-default-text', themeConfig.darkColors.tertiaryText); // White text in dark mode
    root.style.setProperty('--btn-default-border', themeConfig.border.default);
    root.style.setProperty('--btn-outlined-bg', 'transparent');
    root.style.setProperty('--btn-outlined-text', themeConfig.darkColors.tertiaryText); // White text in dark mode
    root.style.setProperty('--btn-outlined-border', themeConfig.border.default);
  } else {
    // Light mode: use lighter backgrounds for default buttons with dark text
    root.style.setProperty('--btn-default-bg', themeConfig.background.white);
    root.style.setProperty('--btn-default-text', themeConfig.text.mutedDark);
    root.style.setProperty('--btn-default-border', themeConfig.border.default);
    root.style.setProperty('--btn-outlined-bg', 'transparent');
    root.style.setProperty('--btn-outlined-text', themeConfig.text.mutedDark);
    root.style.setProperty('--btn-outlined-border', themeConfig.border.default);
  }
  
  // Primary buttons always use primary color with appropriate text
  root.style.setProperty('--btn-primary-bg', colors.primary || themeConfig.colors.primary);
  root.style.setProperty('--btn-primary-text', isDarkMode ? themeConfig.darkColors.tertiaryText : themeConfig.colors.tertiaryText);
};

